/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    ArrowRight,
    ArrowUpRight,
    CheckCircle2,
    Mail,
    MapPin,
    Linkedin,
    AlertCircle,
    Clock,
    Loader2,
} from "lucide-react";
import Navbar from "../components/dmb/Navbar";
import Footer from "../components/dmb/Footer";
import Seo, { breadcrumbTrail } from "../components/dmb/Seo";
import { CONTACT_EMAIL, LINKEDIN, API_BASE } from "../lib/assets";

const CA_OPTIONS = [
    "Moins de 2 M€",
    "2 à 10 M€",
    "10 à 50 M€",
    "50 à 100 M€",
    "100 à 500 M€",
    "Plus de 500 M€",
];
const BESOINS = ["Financement", "Stratégie financière", "Frais bancaires", "Autre"];

const EMPTY_FORM = {
    entreprise: "",
    ca: "2 à 10 M€",
    need: "Financement",
    nom: "",
    fonction: "",
    email: "",
    tel: "",
    message: "",
    consent: false,
    website: "", // honeypot — doit rester vide
};

export default function ContactPage() {
    const [form, setForm] = useState(EMPTY_FORM);
    const [triedSubmit, setTriedSubmit] = useState(false);
    const [status, setStatus] = useState({ state: "idle", msg: "" }); // idle | sending | success | error

    const update = (k) => (e) => {
        const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value ?? e;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const consentError = triedSubmit && !form.consent;

    const onSubmit = async (e) => {
        e.preventDefault();
        setTriedSubmit(true);
        if (!form.consent) return;
        setStatus({ state: "sending", msg: "" });
        try {
            await axios.post(`${API_BASE}/contact`, {
                company_name: form.entreprise,
                revenue: form.ca,
                need: form.need,
                full_name: form.nom,
                job_title: form.fonction,
                email: form.email,
                phone: form.tel,
                message: form.message,
                consent: form.consent,
                website: form.website,
            });
            setStatus({ state: "success", msg: "" });
        } catch (err) {
            const code = err?.response?.status;
            setStatus({
                state: "error",
                msg:
                    code === 429
                        ? "Trop de demandes envoyées. Veuillez réessayer dans une heure."
                        : `Une erreur est survenue lors de l'envoi. Réessayez, ou écrivez-nous directement à ${CONTACT_EMAIL}.`,
            });
        }
    };

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setTriedSubmit(false);
        setStatus({ state: "idle", msg: "" });
    };

    return (
        <div data-testid="contact-page" className="bg-dmb-page">
            <Seo
                title="Contact — Présentez votre projet | DMB Partners"
                description="Financement, stratégie financière ou relation bancaire : quelques lignes suffisent. Un associé de DMB Partners vous répond personnellement sous 24 h ouvrées."
                path="/contact"
                jsonLd={breadcrumbTrail([
                    { name: "Accueil", path: "/" },
                    { name: "Contact", path: "/contact" },
                ])}
            />
            <Navbar />
            <main>
                <section className="bg-white border-b border-dmb-border">
                    <div className="container-dmb pt-14 pb-10 lg:pt-20 lg:pb-14 max-w-[920px]">
                        <div className="surtitre mb-4">Contact</div>
                        <h1>Présentez-nous votre projet.</h1>
                        <p className="mt-6 text-[18px] text-dmb-muted leading-[1.7] max-w-[720px]">
                            Financement, stratégie financière ou relation bancaire :
                            quelques lignes suffisent. Un associé vous répond
                            personnellement.
                        </p>
                    </div>
                </section>

                <section className="section-pad">
                    <div className="container-dmb grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14 items-start">
                        {/* Left column: processus + coords */}
                        <aside className="order-2 lg:order-1 flex flex-col gap-7">
                            <div className="card-dmb p-7">
                                <div className="surtitre mb-5">Ce qui se passe ensuite</div>
                                <ol className="flex flex-col gap-5">
                                    {[
                                        { n: 1, t: "Réponse sous 24 h ouvrées", d: "Edina ou Hugo vous recontacte directement.", Icon: Clock },
                                        { n: 2, t: "Premier échange de 30 minutes", d: "Votre besoin, votre structure, vos banques. Sans engagement.", Icon: Mail },
                                        { n: 3, t: "Proposition d'accompagnement", d: "Un périmètre, un calendrier et des livrables clairs.", Icon: CheckCircle2 },
                                    ].map((s) => (
                                        <li key={s.n} className="flex gap-4">
                                            <div className="w-9 h-9 rounded-full bg-dmb-green-pale text-dmb-green-dark font-bold text-[13px] flex items-center justify-center shrink-0">
                                                {s.n}
                                            </div>
                                            <div>
                                                <div className="font-semibold text-dmb-navy text-[15px]">{s.t}</div>
                                                <div className="text-[14px] text-dmb-muted leading-[1.55] mt-1">{s.d}</div>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            </div>

                            <div className="card-dmb p-7">
                                <div className="surtitre mb-5">Nous joindre</div>
                                <ul className="flex flex-col gap-4 text-[14.5px]">
                                    <li className="flex items-start gap-3">
                                        <Mail className="w-5 h-5 text-dmb-green-dark mt-0.5" />
                                        <a href={`mailto:${CONTACT_EMAIL}`} className="link-green" data-testid="contact-email-link">{CONTACT_EMAIL}</a>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <MapPin className="w-5 h-5 text-dmb-green-dark mt-0.5" />
                                        <span className="text-dmb-navy">149 avenue du Maine, 75014 Paris</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Linkedin className="w-5 h-5 text-dmb-green-dark mt-0.5" />
                                        <a href={LINKEDIN.company} target="_blank" rel="noopener noreferrer" className="link-green inline-flex items-center gap-1" data-testid="contact-linkedin-link">
                                            LinkedIn DMB Partners <ArrowUpRight className="w-3.5 h-3.5" />
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </aside>

                        {/* Right column: form / success */}
                        {status.state === "success" ? (
                            <div
                                data-testid="contact-success"
                                className="order-1 lg:order-2 card-dmb p-8 lg:p-12 flex flex-col items-center text-center gap-5"
                            >
                                <div className="w-14 h-14 rounded-full bg-dmb-green-pale flex items-center justify-center">
                                    <CheckCircle2 className="w-7 h-7 text-dmb-green-dark" />
                                </div>
                                <h2 className="text-[24px] font-bold text-dmb-navy m-0">
                                    Votre demande est envoyée.
                                </h2>
                                <p className="text-[15px] text-dmb-muted leading-[1.7] max-w-[440px] m-0">
                                    Merci {form.nom.trim().split(" ")[0]}. Votre demande
                                    concernant <strong>{form.entreprise}</strong> a bien été
                                    reçue et un accusé de réception vient de vous être
                                    adressé par e-mail. Edina ou Hugo vous recontacte
                                    sous 24 h ouvrées.
                                </p>
                                <button
                                    type="button"
                                    data-testid="contact-send-another"
                                    onClick={resetForm}
                                    className="btn-dmb btn-outline-dark mt-2"
                                >
                                    Envoyer une autre demande
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={onSubmit}
                                noValidate
                                data-testid="contact-form"
                                className="order-1 lg:order-2 card-dmb p-6 lg:p-10 flex flex-col gap-5"
                            >
                                {/* Honeypot anti-spam — invisible pour les humains */}
                                <div
                                    aria-hidden="true"
                                    style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}
                                >
                                    <label htmlFor="website">Site web</label>
                                    <input
                                        id="website"
                                        name="website"
                                        type="text"
                                        value={form.website}
                                        onChange={update("website")}
                                        tabIndex={-1}
                                        autoComplete="off"
                                        data-testid="input-honeypot"
                                    />
                                </div>

                                <Field label="Entreprise *" htmlFor="entreprise">
                                    <input id="entreprise" type="text" required value={form.entreprise} onChange={update("entreprise")} className="field" data-testid="input-entreprise" />
                                </Field>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <Field label="Chiffre d'affaires" htmlFor="ca">
                                        <select id="ca" value={form.ca} onChange={update("ca")} className="field" data-testid="input-ca">
                                            {CA_OPTIONS.map((o) => <option key={o}>{o}</option>)}
                                        </select>
                                    </Field>
                                    <div className="flex flex-col gap-1.5">
                                        <span className="text-[13px] font-semibold text-dmb-navy">Votre besoin</span>
                                        <div role="radiogroup" aria-label="Votre besoin" data-testid="input-need" className="flex flex-wrap gap-2">
                                            {BESOINS.map((o) => {
                                                const selected = form.need === o;
                                                return (
                                                    <button
                                                        key={o}
                                                        type="button"
                                                        role="radio"
                                                        aria-checked={selected}
                                                        data-testid={`need-pill-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                        onClick={() => setForm((f) => ({ ...f, need: o }))}
                                                        className={`h-10 px-4 rounded-full text-[13px] font-semibold border transition-colors ${
                                                            selected
                                                                ? "bg-dmb-navy text-white border-dmb-navy"
                                                                : "bg-white text-dmb-navy border-dmb-border hover:border-dmb-navy"
                                                        }`}
                                                    >
                                                        {o}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <Field label="Nom et prénom *" htmlFor="nom">
                                        <input id="nom" type="text" required value={form.nom} onChange={update("nom")} className="field" data-testid="input-nom" />
                                    </Field>
                                    <Field label="Fonction" htmlFor="fonction">
                                        <input id="fonction" type="text" value={form.fonction} onChange={update("fonction")} className="field" data-testid="input-fonction" />
                                    </Field>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <Field label="E-mail professionnel *" htmlFor="email">
                                        <input id="email" type="email" required value={form.email} onChange={update("email")} className="field" data-testid="input-email" />
                                    </Field>
                                    <Field label="Téléphone *" htmlFor="tel">
                                        <input id="tel" type="tel" required value={form.tel} onChange={update("tel")} className="field" data-testid="input-tel" />
                                    </Field>
                                </div>

                                <Field label="Votre projet en quelques lignes *" htmlFor="message">
                                    <textarea id="message" required rows={6} value={form.message} onChange={update("message")} className="field resize-none" data-testid="input-message" />
                                </Field>

                                <div className={`flex items-start gap-3 p-3 rounded-lg border ${consentError ? "border-dmb-error bg-red-50" : "border-dmb-border bg-dmb-page"}`}>
                                    <input id="consent" type="checkbox" checked={form.consent} onChange={update("consent")} data-testid="input-consent" className="mt-1 w-4 h-4 accent-[#0F1B2D]" />
                                    <label htmlFor="consent" className="text-[14px] text-dmb-navy leading-[1.5]">
                                        J'accepte que DMB Partners utilise ces informations
                                        pour me recontacter au sujet de ma demande.{" "}
                                        <span className="text-dmb-error">*</span>
                                    </label>
                                </div>
                                {consentError && (
                                    <div data-testid="consent-error" className="flex items-center gap-2 text-[13px] text-dmb-error font-medium">
                                        <AlertCircle className="w-4 h-4" />
                                        Veuillez cocher cette case pour pouvoir envoyer votre demande.
                                    </div>
                                )}

                                {status.state === "error" && (
                                    <div data-testid="contact-error" className="flex items-center gap-2 p-3 rounded-lg border border-dmb-error bg-red-50 text-[13.5px] text-dmb-error font-medium">
                                        <AlertCircle className="w-4 h-4 shrink-0" />
                                        {status.msg}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    data-testid="contact-submit"
                                    disabled={status.state === "sending"}
                                    className="btn-dmb btn-primary justify-center disabled:opacity-60"
                                >
                                    {status.state === "sending" ? (
                                        <>
                                            Envoi en cours
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        </>
                                    ) : (
                                        <>
                                            Envoyer ma demande
                                            <ArrowRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>

                                <p className="text-[12px] text-dmb-muted-2 leading-[1.6]">
                                    * Champs obligatoires. Vos données sont traitées par
                                    DMB PARTNERS pour répondre à votre demande et assurer
                                    le suivi de la relation commerciale. Elles sont
                                    conservées 3 ans après le dernier contact et ne sont
                                    jamais cédées. Vous pouvez exercer vos droits
                                    d'accès, de rectification, d'effacement et
                                    d'opposition à{" "}
                                    <a href={`mailto:${CONTACT_EMAIL}`} className="link-green">{CONTACT_EMAIL}</a>.{" "}
                                    <Link to="/confidentialite" className="link-green">Politique de confidentialité</Link>.
                                </p>
                            </form>
                        )}
                    </div>
                </section>
            </main>
            <Footer />

            <style>{`
                .field {
                    width: 100%;
                    min-height: 48px;
                    padding: 10px 14px;
                    border: 1px solid var(--dmb-border);
                    background: #fff;
                    color: var(--dmb-navy);
                    border-radius: 8px;
                    font-size: 15px;
                    font-family: inherit;
                    transition: border-color 150ms ease, box-shadow 150ms ease;
                }
                .field:focus {
                    outline: none;
                    border-color: var(--dmb-green);
                    box-shadow: 0 0 0 3px rgba(78, 174, 50, 0.15);
                }
                textarea.field { min-height: 140px; }
            `}</style>
        </div>
    );
}

function Field({ label, htmlFor, children }) {
    return (
        <div className="flex flex-col gap-1.5">
            <label htmlFor={htmlFor} className="text-[13px] font-semibold text-dmb-navy">{label}</label>
            {children}
        </div>
    );
}
